"use client";

import { IconChevronDownOutline18, IconChevronRightOutline18 } from "nucleo-ui-outline-18";
import { NodeIconTile } from "@/components/ui/node-icon-tile";
import { useDefinitionByType } from "@/hooks/useActionTriggerDefinitions";
import { useDescriptionFormatHints } from "@/hooks/useDescriptionFormatHints";
import { useResolvedNodeVisual } from "@/hooks/useResolvedNodeVisual";
import { useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { cn } from "@/lib/utils";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { getRequiredConnectorType } from "@/utils/workflowTransform";
import { ReactNode, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { resolveDescriptionTemplates, NATIVE_ONLY_ACTION_TYPES } from "@/utils/resolveDescriptionTemplates";
import { type InlineTokenData } from "./components/InlineAddressLink";
import { ResolvedDescriptionContent } from "./components/ResolvedDescriptionContent";
import { DexSwapPayload } from "./components/payloads/DexSwapPayload";
import { EvmLogPayload } from "./components/payloads/EvmLogPayload";
import { GenericPayload } from "./components/payloads/GenericPayload";
import { MathOperationPayload } from "./components/payloads/MathOperationPayload";
import { SendErc20TokenPayload } from "./components/payloads/SendErc20TokenPayload";
import { EVM_LOG_TRIGGER_TYPE } from "./utils/constants";
const SEND_ERC20_TOKEN_TYPE = "send-erc20-token";
const DEX_SWAP_TYPE = "v2-swap";

/**
 * Helper function to detect if payload is a math operation
 */
function isMathOperationPayload(payload?: Record<string, unknown>): boolean {
  if (!payload) return false;
  const keys = Object.keys(payload).map(k => k.toLowerCase());
  return keys.includes("left") && keys.includes("operator") && keys.includes("right");
}
export interface WorkflowBranch {
  label: string;
  color: "green" | "red" | "purple" | "amber";
  steps: WorkflowStep[];
  /** Whether this branch should be expanded by default (e.g., because it ran) */
  defaultExpanded?: boolean;
}
export interface WorkflowStep {
  id: string;
  name: string;
  type: string;
  isTrigger: boolean;
  icon?: ReactNode;
  badge?: string;
  description?: string;
  payload?: Record<string, unknown>;
  /** Connector instance attached to this node (e.g., Turnkey wallet) */
  connector?: {
    id?: string;
    type: string;
  };
  /** Pre-resolved connector/wallet display name (avoids per-row store subscriptions) */
  connectorLabel?: string;
  /** Pre-resolved wallet address for display (avoids per-row store subscriptions) */
  walletAddress?: string;
  /** Raw titleOverride template — resolved in step-row with token format hints */
  titleOverride?: string;
  /** Child branches for logic nodes (if-then-else, for-each) */
  branches?: WorkflowBranch[];
}
interface WorkflowStepRowProps {
  step: WorkflowStep;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  totalSteps: number;
  /**
   * Whether to link to the integration detail page
   * @default true
   */
  linkToDetail?: boolean;
  /**
   * Whether step content is expanded by default
   * @default true
   */
  defaultExpanded?: boolean;
}

/**
 * WorkflowStepRow - Apple Shortcuts-inspired workflow step display
 *
 * Displays a single workflow step with:
 * - Vertical connecting line on the left
 * - Circle indicator for each step
 * - Icon and badge
 * - Step name and type
 * - Payload information (specialized for EVM Log Triggers or generic)
 */
export function WorkflowStepRow({
  step,
  isFirst,
  isLast,
  linkToDetail = true,
  defaultExpanded = true
}: WorkflowStepRowProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const isLogic = !!step.branches;
  const isEvmLogTrigger = step.type === EVM_LOG_TRIGGER_TYPE;
  const isSendErc20Token = step.type === SEND_ERC20_TOKEN_TYPE;
  const isDexSwap = step.type === DEX_SWAP_TYPE;
  const isMathOperation = isMathOperationPayload(step.payload);
  const integrationType = step.isTrigger ? "trigger" : "action";
  const detailUrl = `/integrations/${integrationType}/${step.type}`;
  const badge = step.badge || (step.isTrigger ? "Trigger" : "Action");

  // Look up connector info to show the platform icon (e.g., Slack logo)
  const definition = useDefinitionByType(step.type);
  const iconVisual = useResolvedNodeVisual(step.type);
  // PERF: useShallow prevents all N rows re-rendering when connectorTypes array ref changes
  const connectorTypes = useConnectorsStore(useShallow(state => state.connectorTypes));
  const requiredConnectorType = getRequiredConnectorType(step.type, definition);
  const connectorTypeDefinition = requiredConnectorType ? connectorTypes.find(ct => ct.type === requiredConnectorType) : undefined;
  const connectorProp = requiredConnectorType ? {
    type: requiredConnectorType,
    logoUrl: connectorTypeDefinition?.logoUrl
  } : step.connector?.type ? {
    type: step.connector.type
  } : undefined;

  // Use pre-resolved connector label from parent (avoids N store subscriptions)
  const connectorLabel = step.connectorLabel || connectorTypeDefinition?.name || step.connector?.type;

  // Extract token info from payload for badge display
  const tokenAddress = step.payload?.tokenAddress as string | undefined;
  const tokenInAddress = step.payload?.tokenIn as string | undefined;
  const tokenOutAddress = step.payload?.tokenOut as string | undefined;
  const sellTokenAddress = step.payload?.sellToken as string | undefined;
  const buyTokenAddress = step.payload?.buyToken as string | undefined;
  const chainId = step.payload?.chainId as number | undefined;
  const srcChainId = step.payload?.srcChainId as number | undefined;
  const dstChainId = step.payload?.dstChainId as number | undefined;
  const tokenInChainId = srcChainId || chainId;
  const tokenOutChainId = dstChainId || chainId;
  const isNativeOnlyAction = NATIVE_ONLY_ACTION_TYPES.includes(step.type as (typeof NATIVE_ONLY_ACTION_TYPES)[number]);
  const shouldFetchTokenInfo = !!(tokenAddress && chainId && tokenAddress.startsWith("0x"));
  const shouldFetchTokenIn = !!(tokenInAddress && tokenInChainId && tokenInAddress.startsWith("0x"));
  const shouldFetchTokenOut = !!(tokenOutAddress && tokenOutChainId && tokenOutAddress.startsWith("0x"));
  const shouldFetchSellToken = !!(sellTokenAddress && chainId && sellTokenAddress.startsWith("0x"));
  const shouldFetchBuyToken = !!(buyTokenAddress && chainId && buyTokenAddress.startsWith("0x"));
  const {
    data: simTokenData
  } = useSimTokenInfo(tokenAddress, chainId, shouldFetchTokenInfo);
  const {
    data: simTokenInData
  } = useSimTokenInfo(tokenInAddress, tokenInChainId, shouldFetchTokenIn);
  const {
    data: simTokenOutData
  } = useSimTokenInfo(tokenOutAddress, tokenOutChainId, shouldFetchTokenOut);
  const {
    data: simSellTokenData
  } = useSimTokenInfo(sellTokenAddress, chainId, shouldFetchSellToken);
  const {
    data: simBuyTokenData
  } = useSimTokenInfo(buyTokenAddress, chainId, shouldFetchBuyToken);

  // Convert token data to InlineTokenData format for inline token badge display
  const tokensMap = useMemo(() => {
    const tokenEntries = [{
      address: tokenAddress,
      data: simTokenData
    }, {
      address: tokenInAddress,
      data: simTokenInData
    }, {
      address: tokenOutAddress,
      data: simTokenOutData
    }, {
      address: sellTokenAddress,
      data: simSellTokenData
    }, {
      address: buyTokenAddress,
      data: simBuyTokenData
    }].filter((entry): entry is {
      address: string;
      data: NonNullable<typeof simTokenData>;
    } => !!entry.address && !!entry.data?.tokens?.[0]).map(({
      address,
      data
    }) => {
      const token = data.tokens[0];
      return [address.toLowerCase(), {
        address,
        symbol: token.symbol,
        name: token.name || token.symbol,
        chainId: token.chain_id,
        logoUrl: token.logo || token.token_metadata?.logo
      } satisfies InlineTokenData] as const;
    });
    return tokenEntries.length > 0 ? new Map(tokenEntries) : undefined;
  }, [simTokenData, simTokenInData, simTokenOutData, simSellTokenData, simBuyTokenData, tokenAddress, tokenInAddress, tokenOutAddress, sellTokenAddress, buyTokenAddress]);

  // Build format hints for description template resolution
  const formatHints = useDescriptionFormatHints({
    simTokenData,
    simTokenInData,
    simSellTokenData,
    tokenInAddress,
    tokenInChainId,
    isNativeOnlyAction,
    chainId
  });
  const showLine = !isLast;
  const iconColorHex = isLogic ? "#a855f7" : step.isTrigger ? "#2450FF" : "#22c55e";
  const hasConnectorInfo = !!(connectorLabel && step.connector);
  const hasContent = !!step.description || step.payload && Object.keys(step.payload).length > 0 || hasConnectorInfo;
  return <div>
      {/* Header row: dot + icon + name */}
      <div className={cn("relative flex items-center gap-4", hasContent && "cursor-pointer")} onClick={hasContent ? () => setIsExpanded(!isExpanded) : undefined}>
        {/* Dotted line connectors behind the circle */}
        {!isFirst && <div className="border-ds-neutral-300 absolute bottom-1/2 left-[11px] top-0 w-0 border-l-2 border-dashed dark:border-zinc-600" />}
        {showLine && <div className="border-ds-neutral-300 absolute bottom-0 left-[11px] top-1/2 w-0 border-l-2 border-dashed dark:border-zinc-600" />}

        {/* Colored dot — radial gradient ring + 3D glossy inner */}
        <div className={cn("relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full", isLogic ? "bg-[radial-gradient(circle_at_50%_35%,#e9d5ff_0%,#f3e8ff_55%,#faf5ff_100%)] dark:bg-[radial-gradient(circle_at_50%_35%,rgba(168,85,247,0.35)_0%,rgba(168,85,247,0.15)_60%,rgba(168,85,247,0.05)_100%)]" : step.isTrigger ? "bg-[radial-gradient(circle_at_50%_35%,#bfdbfe_0%,#dbeafe_55%,#eff6ff_100%)] dark:bg-[radial-gradient(circle_at_50%_35%,rgba(37,80,255,0.35)_0%,rgba(37,80,255,0.15)_60%,rgba(37,80,255,0.05)_100%)]" : "bg-[radial-gradient(circle_at_50%_35%,#bbf7d0_0%,#dcfce7_55%,#f0fdf4_100%)] dark:bg-[radial-gradient(circle_at_50%_35%,rgba(34,197,94,0.35)_0%,rgba(34,197,94,0.15)_60%,rgba(34,197,94,0.05)_100%)]")}>
          <div className={cn("h-2.5 w-2.5 rounded-full bg-gradient-to-b shadow-[inset_0_0.5px_0.5px_rgba(255,255,255,0.6),0_1px_1.5px_rgba(17,23,46,0.25)]", isLogic ? "from-purple-400 to-purple-600" : step.isTrigger ? "from-blue-400 to-blue-600" : "from-green-400 to-green-600")} />
        </div>

        {/* Node icon — allow direct definition logos for triggers without connectors (e.g. Polymarket) */}
        <NodeIconTile size="md" id={step.type} subject={step.name} context="" logoUrl={iconVisual.logoUrl} connector={connectorProp ?? iconVisual.connector} alt={step.name} color={iconColorHex} />

        {/* Name + connector + description */}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="body-sm-semibold">
              {resolveDescriptionTemplates(step.titleOverride, step.payload, formatHints) || step.name}
            </span>
          </div>

          {/* Description */}
          {step.description && <div className="text-ds-text-subtle text-sm leading-relaxed">
              <ResolvedDescriptionContent text={step.description} payload={step.payload} formatHints={formatHints} tokens={tokensMap ? Array.from(tokensMap.values()) : undefined} />
            </div>}
        </div>

        {/* Accordion chevron */}
        {hasContent && <span className="text-ds-text-disabled flex-shrink-0">
            {isExpanded ? <IconChevronDownOutline18 className="h-3.5 w-3.5" /> : <IconChevronRightOutline18 className="h-3.5 w-3.5" />}
          </span>}
      </div>
      {/* Content below header with dotted line rail */}
      <div className="flex gap-4">
        {/* Dotted line column — centered under the dot */}
        <div className="flex w-6 flex-shrink-0 flex-col items-center">
          {showLine && <div className="border-ds-neutral-300 w-px flex-1 border-l-2 border-dashed dark:border-zinc-600" />}
        </div>

        {/* Content + bottom spacing */}
        <div className={cn("min-w-0 flex-1", showLine ? "pb-8" : "pb-2")}>
          {isExpanded && hasContent && <div className="mt-2 space-y-3 pl-3">
              {/* Connector info */}
              {hasConnectorInfo && <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">
                      Connector:
                    </span>
                    <span className="text-ds-text-primary inline-flex items-center gap-1.5 font-medium">
                      {connectorTypeDefinition?.logoUrl && <img src={connectorTypeDefinition.logoUrl} alt="" className="h-3.5 w-3.5 rounded-full" />}
                      {connectorLabel}
                    </span>
                  </div>
                  {step.walletAddress && <div className="flex items-center gap-2">
                      <span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">
                        Wallet:
                      </span>
                      <span className="text-ds-text-primary font-mono">
                        {step.walletAddress.slice(0, 6)}...{step.walletAddress.slice(-4)}
                      </span>
                    </div>}
                </div>}

              {/* Payload - Route to specialized component based on step type */}
              {isEvmLogTrigger ? <EvmLogPayload chainId={step.payload?.chainId as number | undefined} contractAddress={step.payload?.contractAddress as string | undefined} eventAbi={step.payload?.eventAbi as string | undefined} filter={step.payload?.filter} /> : isSendErc20Token ? <SendErc20TokenPayload tokenAddress={step.payload?.tokenAddress as string | undefined} chainId={step.payload?.chainId as number | undefined} recipientAddress={step.payload?.recipientAddress as string | undefined} amount={step.payload?.amount as string | number | undefined} /> : isDexSwap ? <DexSwapPayload tokenIn={step.payload?.tokenIn as string | undefined} tokenOut={step.payload?.tokenOut as string | undefined} amountIn={step.payload?.amountIn as string | number | undefined} chainId={step.payload?.chainId as number | undefined} deadlineMinutes={step.payload?.deadlineMinutes as number | undefined} slippageBps={step.payload?.slippageBps as number | undefined} /> : isMathOperation ? <MathOperationPayload left={step.payload?.left ?? step.payload?.Left} operator={step.payload?.operator ?? step.payload?.Operator} right={step.payload?.right ?? step.payload?.Right} rounding={step.payload?.rounding ?? step.payload?.Rounding} /> : step.payload && <GenericPayload payload={step.payload} connector={step.connector} />}
            </div>}
        </div>
      </div>
    </div>;
}

// Re-export types and utilities for external use
export { ChainBadge } from "./components/ChainBadge";
export { MathOperationPayload } from "./components/payloads/MathOperationPayload";
export { useChainInfo, useChainInfoMap, type ChainInfo } from "@/app/(v2)/workflows/_hooks/useChainInfo";
export { formatDelayTime, formatFilter, formatRRule, formatTokenAmount } from "./utils/formatters";